/**
 * usePermissions Hook
 * Manages permission state and operations
 */

import { useState, useCallback, useMemo } from 'react';
import { PERMISSION_ACTIONS } from '../utils/permissionHelpers';

const getPermissionKey = (roleId, module, action) => `${roleId}-${module}_${action}`;

const getPermissionValue = (role, changes, roleId, module, action) => {
  const key = getPermissionKey(roleId, module, action);
  if (key in changes) {
    return changes[key];
  }
  return role?.permissions?.[module]?.[action] === true;
};

/**
 * Enforces view dependency rule:
 * If any action other than 'view' is enabled for a module,
 * then 'view' must automatically be enabled.
 * This ensures users can't perform actions on modules they can't view.
 */
const enforceViewDependency = (changes, role, roleId, module) => {
  // Get all available actions for this module
  const moduleActions = PERMISSION_ACTIONS[module];
  if (!moduleActions || moduleActions.length === 0) {
    return changes;
  }

  // Check if any non-view action is enabled (either in original permissions or in changes)
  const hasNonViewActionEnabled = moduleActions.some((action) => {
    // Skip 'view' action itself
    if (action === 'view') {
      return false;
    }
    // Check if this action is enabled
    return getPermissionValue(role, changes, roleId, module, action);
  });

  // If no non-view action is enabled, no need to enforce view
  if (!hasNonViewActionEnabled) {
    return changes;
  }

  // At least one non-view action is enabled, so view must be enabled
  const viewKey = getPermissionKey(roleId, module, 'view');
  const originalView = role?.permissions?.[module]?.view === true;

  // If view was originally enabled and user tries to disable it, remove that change
  if (originalView) {
    if (changes[viewKey] === false) {
      const updatedChanges = { ...changes };
      delete updatedChanges[viewKey];
      return updatedChanges;
    }
    return changes;
  }

  // If view was not originally enabled, automatically enable it
  if (changes[viewKey] !== true) {
    return {
      ...changes,
      [viewKey]: true
    };
  }

  return changes;
};

export const usePermissions = (roles) => {
  const [permissionChanges, setPermissionChanges] = useState({});

  /**
   * Checks if a role has a specific permission
   */
  const hasPermission = useCallback((roleId, permissionId) => {
    // First check if there's an unsaved change
    const changeKey = `${roleId}-${permissionId}`;
    if (changeKey in permissionChanges) {
      return permissionChanges[changeKey];
    }

    // Fall back to role's actual permissions
    const role = roles.find(r => r.id === roleId);
    if (!role?.permissions || typeof role.permissions !== 'object') {
      return false;
    }
    
    // Parse permission ID (format: "module_action" where module can have underscores)
    // We need to match against known modules from PERMISSION_ACTIONS
    // Try longest module names first to handle modules with underscores like "roles_and_permissions"
    const moduleNames = Object.keys(PERMISSION_ACTIONS).sort((a, b) => b.length - a.length);
    
    let module = null;
    let action = null;
    
    for (const moduleName of moduleNames) {
      if (permissionId.startsWith(`${moduleName}_`)) {
        module = moduleName;
        action = permissionId.substring(moduleName.length + 1);
        break;
      }
    }
    
    if (!module || !action) return false;
    
    return role.permissions[module]?.[action] === true;
  }, [roles, permissionChanges]);

  /**
   * Helper function to parse permission ID into module and action
   * Handles modules with underscores like "roles_and_permissions"
   */
  const parsePermissionId = useCallback((permissionId) => {
    // Try longest module names first to handle modules with underscores
    const moduleNames = Object.keys(PERMISSION_ACTIONS).sort((a, b) => b.length - a.length);
    
    for (const moduleName of moduleNames) {
      if (permissionId.startsWith(`${moduleName}_`)) {
        return {
          module: moduleName,
          action: permissionId.substring(moduleName.length + 1)
        };
      }
    }
    
    return { module: null, action: null };
  }, []);

  /**
   * Toggles a single permission
   */
  const togglePermission = useCallback((roleId, permissionId) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) return;

    const changeKey = `${roleId}-${permissionId}`;
    const currentValue = hasPermission(roleId, permissionId);
    const newValue = !currentValue;
    
    // Get original value using proper parsing
    const { module, action } = parsePermissionId(permissionId);
    if (!module || !action) return;
    
    const originalValue = role.permissions[module]?.[action] === true;
    
    setPermissionChanges(prev => {
      let updated = { ...prev };

      // Remove if toggling back to original state
      if (newValue === originalValue) {
        delete updated[changeKey];
      } else {
        updated[changeKey] = newValue;
      }

      updated = enforceViewDependency(updated, role, roleId, module);

      return updated;
    });
  }, [roles, hasPermission, parsePermissionId]);

  /**
   * Toggles all permissions in a module
   */
  const toggleModuleForRole = useCallback((roleId, modulePermissions) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) return;

    const allEnabled = modulePermissions.every(perm => hasPermission(roleId, perm.id));
    
    setPermissionChanges(prev => {
      let updated = { ...prev };

      modulePermissions.forEach(perm => {
        const changeKey = `${roleId}-${perm.id}`;
        const { module, action } = parsePermissionId(perm.id);
        if (!module || !action) return;
        
        const originalValue = role.permissions[module]?.[action] === true;
        const newValue = !allEnabled;

        if (newValue === originalValue) {
          delete updated[changeKey];
        } else {
          updated[changeKey] = newValue;
        }
      });

      const moduleName = modulePermissions[0]?.module;
      if (moduleName) {
        updated = enforceViewDependency(updated, role, roleId, moduleName);
      }

      return updated;
    });
  }, [roles, hasPermission, parsePermissionId]);

  /**
   * Discards all changes
   */
  const discardChanges = useCallback(() => {
    setPermissionChanges({});
  }, []);

  /**
   * Gets count of unsaved changes
   */
  const unsavedChangesCount = useMemo(() => {
    return Object.keys(permissionChanges).length;
  }, [permissionChanges]);

  /**
   * Builds update payload for API
   */
  const getUpdatePayload = useCallback(() => {
    const changesByRole = {};
    
    Object.entries(permissionChanges).forEach(([key, hasPermissionValue]) => {
      const [roleId, ...rest] = key.split('-');
      const permissionId = rest.join('-');
      
      if (!changesByRole[roleId]) {
        changesByRole[roleId] = {};
      }

      const { module, action } = parsePermissionId(permissionId);
      if (!module || !action) return;
      
      if (!changesByRole[roleId][module]) {
        changesByRole[roleId][module] = {};
      }
      
      changesByRole[roleId][module][action] = hasPermissionValue;
    });

    return changesByRole;
  }, [permissionChanges, parsePermissionId]);

  return {
    permissionChanges,
    hasPermission,
    togglePermission,
    toggleModuleForRole,
    discardChanges,
    unsavedChangesCount,
    getUpdatePayload
  };
};

